import { pdf } from '@react-pdf/renderer';
import React from 'react';

import { FILE_TYPES } from '@commons/utils/fileUtils';

import ImageInvoicePdf from '../components/ImageInvoicePdf';

const PDF_FILE_EXTENSION = '.pdf';
const PDF_FILE_EXTENSION_REGEX = /\.[^/.]+$/;
const PDF_MIME_TYPE = FILE_TYPES.documents.mime.pdf;
const IMAGE_MIME_TYPES = [FILE_TYPES.images.mime.jpeg, FILE_TYPES.images.mime.png];
const LANDSCAPE_ORIENTATION = 'landscape';
const PORTRAIT_ORIENTATION = 'portrait';
const COMPRESSED_IMAGE_MIME_TYPE = 'image/jpeg';
const COMPRESSED_IMAGE_QUALITY = 0.9;

export const isInvoiceControlImageFile = (file) => IMAGE_MIME_TYPES.includes(file?.type);

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result);
    };

    reader.onerror = () => {
      reject(reader.error);
    };

    reader.readAsDataURL(file);
  });

const getPdfFileName = (fileName) => {
  if (!fileName) {
    return `invoice${PDF_FILE_EXTENSION}`;
  }

  if (!PDF_FILE_EXTENSION_REGEX.test(fileName)) {
    return `${fileName}${PDF_FILE_EXTENSION}`;
  }

  return fileName.replace(PDF_FILE_EXTENSION_REGEX, PDF_FILE_EXTENSION);
};

const compressImageAsJpeg = (imageSource) =>
  new Promise((resolve, reject) => {
    const image = new window.Image();

    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;

      const context = canvas.getContext('2d');

      if (!context) {
        reject(new Error('Unable to create canvas 2D context'));
        return;
      }

      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0);

      const compressedSource = canvas.toDataURL(
        COMPRESSED_IMAGE_MIME_TYPE,
        COMPRESSED_IMAGE_QUALITY,
      );
      const orientation =
        image.naturalWidth > image.naturalHeight ? LANDSCAPE_ORIENTATION : PORTRAIT_ORIENTATION;

      resolve({ compressedSource, orientation });
    };

    image.onerror = () => {
      reject(new Error('Unable to load invoice image'));
    };

    image.src = imageSource;
  });

export const convertInvoiceControlImageToPdf = async (file) => {
  if (!isInvoiceControlImageFile(file)) {
    return file;
  }

  const imageSource = await readFileAsDataUrl(file);
  const { compressedSource, orientation } = await compressImageAsJpeg(imageSource);
  const pdfBlob = await pdf(
    <ImageInvoicePdf imageSource={compressedSource} orientation={orientation} />,
  ).toBlob();

  return new File([pdfBlob], getPdfFileName(file.name), {
    type: PDF_MIME_TYPE,
  });
};
